/**
 * apps/command/src/screens/BroadcastScreen.tsx — §6.5's «Jonli eʼlon» composer.
 *
 * ============================================================================
 * "UNDER 60 SECONDS FROM TYPING TO ON-SCREEN CITY-WIDE." (§6.5)
 * ============================================================================
 *
 * That number is the design brief, and it is what decides the layout: severity,
 * text, target, send — visible together, on one screen, with no step that can
 * be skipped and no step that has to be found. An operator writing an
 * evacuation notice at 3 a.m. does not navigate.
 *
 * So the composer is ABOVE the history rather than behind a «New» button. The
 * screen you land on is the screen you type into.
 *
 * ============================================================================
 * THE DANGEROUS HALF: 35 PUBLIC SCREENS, IN ONE MINUTE.
 * ============================================================================
 *
 * A system that fast can also broadcast the wrong thing that fast, so three
 * things are deliberately in the way — and none of them is a confirmation
 * dialog, because a dialog at 3 a.m. is a reflex rather than a decision:
 *
 *   THE PREVIEW IS THE SEND BUTTON'S NEIGHBOUR. §6.5 asks for a preview "at
 *   true 1080×1920"; what fits here is the same content in the same treatment
 *   the kiosk will use, scaled — so the thing being approved is the thing that
 *   ships.
 *
 *   THE TARGET IS STATED IN WORDS, NOT INFERRED FROM AN EMPTY FIELD. "Butun
 *   shahar" is written on the control, because the city-wide case is the
 *   DEFAULT and a default that looks like an empty selection is how somebody
 *   broadcasts to 35 screens believing they picked one.
 *
 *   MISSING LOCALES ARE NAMED BEFORE SENDING, not after. §6.4 wants a missing
 *   translation flagged in the composer — the difference between "we chose not
 *   to translate at 3 a.m." and "this screen is broken" is whether the operator
 *   was told.
 *
 * ============================================================================
 * WHAT IS NOT HERE.
 * ============================================================================
 *
 * §6.2's ADS. Not built: §18 risk 5 rules that commercial advertising on a
 * hokimlik screen "is a political decision, not a technical one — build the
 * capability; get written policy before the first paid campaign". A media
 * pipeline nobody is allowed to use yet would be the wrong thing to put in
 * front of an operator on this screen.
 *
 * §6.6's emergency TEMPLATES and voice-to-announcement. Both are real
 * accelerators of this exact flow and both are worth having; neither is needed
 * for the sixty seconds to work.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ANNOUNCEMENT_DEFAULT_HOURS,
  ANNOUNCEMENT_IMAGE_MIME,
  ANNOUNCEMENT_VIDEO_WARN_BYTES,
  ANNOUNCEMENT_PRESENTATION,
  ANNOUNCEMENT_SEVERITIES,
  ANNOUNCEMENT_VIDEO_MIME,
  KIOSK_SCREEN_HEIGHT,
  KIOSK_SCREEN_WIDTH,
  type AnnouncementSeverity,
} from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap, uploadMbtMedia, type UploadedMbtMedia } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useFleet } from '../realtime/useFleet.js';
import { hasKey, t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import {
  Button,
  EmptyState,
  Field,
  Notice,
  Panel,
  PanelHeader,
  Skeleton,
  TextInput,
} from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';

type MbtAnnouncement = components['schemas']['MbtAnnouncement'];

/** The four locales, in the order the kiosk's own switcher shows them. */
const LOCALE_FIELDS = [
  { key: 'uz_latn', labelKey: 'broadcast.locale.uzLatn', required: true },
  { key: 'uz_cyrl', labelKey: 'broadcast.locale.uzCyrl', required: false },
  { key: 'ru', labelKey: 'broadcast.locale.ru', required: false },
  { key: 'en', labelKey: 'broadcast.locale.en', required: false },
] as const;

type LocaleKey = (typeof LOCALE_FIELDS)[number]['key'];
type TextMap = Partial<Record<LocaleKey, string>>;

function severityLabel(severity: string): string {
  return hasKey(`broadcast.severity.${severity}`)
    ? t(`broadcast.severity.${severity}`)
    : severity;
}

/** The accent a severity carries, from the domain — never chosen here. */
function accentClass(severity: AnnouncementSeverity): string {
  const accent = ANNOUNCEMENT_PRESENTATION[severity].accent;
  return accent === 'crit' ? 'text-crit' : accent === 'warn' ? 'text-warn' : 'text-brand';
}

export function BroadcastScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  /*
    HOW MANY SCREENS THIS WILL APPEAR ON, live.

    «Butun shahar — barcha faol kiosklar» with no number let somebody publish a
    province-wide takeover without ever seeing the scale of it. From the same
    fleet feed the grid uses, so it cannot drift from what the request will hit.
  */
  const fleet = useFleet(onExpired);
  const targetCount = fleet.kiosks.length;
  const mayPublish = allows('mbt.broadcast.publish');

  const [items, setItems] = useState<MbtAnnouncement[] | null>(null);
  const [severity, setSeverity] = useState<AnnouncementSeverity>('warning');
  /** Which language tab is open. Starts on the required one. */
  const [locale, setLocale] = useState<string>(LOCALE_FIELDS[0]?.key ?? 'uz_latn');
  const [title, setTitle] = useState<TextMap>({});
  const [body, setBody] = useState<TextMap>({});
  const [hours, setHours] = useState(String(ANNOUNCEMENT_DEFAULT_HOURS));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState<UploadedMbtMedia | null>(null);
  const [uploading, setUploading] = useState(false);
  /** Set when the chosen file is big enough to delay its first showing. */
  const [heavy, setHeavy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/announcements', {}));
      setItems(data.items as MbtAnnouncement[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    /**
     * Ten seconds, not thirty. This screen's whole purpose is the minute after
     * a send — an operator is watching kiosks confirm one by one, and §6.4's
     * delivery board is only useful if it moves while they are looking at it.
     */
    const timer = setInterval(() => void load(), 10_000);
    return () => clearInterval(timer);
  }, [load]);

  /** The languages this notice will NOT be read in, computed as they type. */
  const missing = LOCALE_FIELDS.filter(
    (field) => !field.required && (!title[field.key]?.trim() || !body[field.key]?.trim()),
  ).map((field) => t(field.labelKey));

  async function send(): Promise<void> {
    if (!title.uz_latn?.trim() || !body.uz_latn?.trim()) {
      setError(t('broadcast.uzLatnRequired'));
      return;
    }
    const windowHours = Number(hours);
    if (!Number.isFinite(windowHours) || windowHours <= 0) {
      setError(t('broadcast.expiryRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api().POST('/mbt/announcements', {
        body: {
          severity,
          title: { uz_latn: title.uz_latn.trim(), ...cleaned(title) },
          body: { uz_latn: body.uz_latn.trim(), ...cleaned(body) },
          expires_at: new Date(Date.now() + windowHours * 3_600_000).toISOString(),
          ...(media ? { media_id: media.media_id } : {}),
        },
      });
      setTitle({});
      setBody({});
      setMedia(null);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) {
        setError(t('broadcast.noPermission'));
      } else setError(t('broadcast.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Upload first, publish second.
   *
   * §6.5 asks the operator to preview at true 1080×1920 before sending, and you
   * cannot preview a file the server has not seen: the image the kiosk shows is
   * the RE-ENCODED one, fitted inside the screen, not the original the operator
   * chose. Previewing the local File would be a drawing of what the kiosk might
   * do — this previews the thing itself.
   */
  async function pickMedia(file: File | undefined): Promise<void> {
    if (!file) return;
    setHeavy(file.type.startsWith('video/') && file.size > ANNOUNCEMENT_VIDEO_WARN_BYTES);
    setUploading(true);
    setError(null);
    try {
      setMedia(await uploadMbtMedia(file));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 413) {
        setError(t('broadcast.mediaTooLarge'));
      } else if (caught instanceof ApiError && caught.status === 415) {
        setError(t('broadcast.mediaWrongType'));
      } else setError(t('broadcast.mediaFailed'));
    } finally {
      setUploading(false);
    }
  }

  async function takedown(id: string): Promise<void> {
    setBusy(true);
    try {
      await api().POST('/mbt/announcements/{announcement_id}/takedown', {
        params: { path: { announcement_id: id } },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('broadcast.takedownFailed'));
    } finally {
      setBusy(false);
    }
  }

  const liveNotices = (items ?? []).filter((notice) => notice.live);
  const deliveredTotal = (items ?? []).reduce(
    (sum, notice) => sum + (notice.delivered?.length ?? 0),
    0,
  );
  const pendingTotal = (items ?? []).reduce(
    (sum, notice) => sum + (notice.pending?.length ?? 0),
    0,
  );
  const deferredTotal = (items ?? []).reduce(
    (sum, notice) =>
      sum + (notice.delivered ?? []).filter((row) => row.deferred_by_appeal).length,
    0,
  );

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('broadcast.eyebrow')}</span>}
        title={t('nav.broadcast')}
        subtitle={t('broadcast.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {liveNotices.length}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('broadcast.kpiLive')}
          value={String(liveNotices.length)}
          absent={t('app.notYet')}
          tone={liveNotices.length > 0 ? 'warn' : 'ok'}
          footerLabel={t('broadcast.kpiTotal')}
          footerValue={String(items?.length ?? 0)}
        />
        <KpiCard
          label={t('broadcast.kpiDelivered')}
          value={String(deliveredTotal)}
          absent={t('app.notYet')}
          tone="ok"
          footerLabel={t('broadcast.kpiPending')}
          footerValue={String(pendingTotal)}
        />
        <KpiCard
          label={t('broadcast.kpiCoverage')}
          /*
            §6.4's real measure. The denominator is delivered PLUS pending, so
            a notice nobody has confirmed reads as 0% rather than as an empty
            figure — «no data» and «no screen showed it» are the same emergency.
          */
          value={
            deliveredTotal + pendingTotal === 0
              ? null
              : `${Math.round((deliveredTotal / (deliveredTotal + pendingTotal)) * 100)}%`
          }
          absent={t('app.notYet')}
          tone={pendingTotal > 0 ? 'warn' : 'ok'}
          footerLabel={t('broadcast.kpiPendingMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('broadcast.kpiDeferred')}
          /* §3.1 reporting in. Not a failure — a screen that showed the notice
             a minute late because a citizen was mid-murojaat — and counted
             apart so it never reads as one. */
          value={String(deferredTotal)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('broadcast.kpiDeferredMeaning')}
          footerValue=""
        />
      </div>

      {mayPublish ? (
        <Panel>
          <PanelHeader title={t('broadcast.composeTitle')} aside={t('broadcast.composeHint')} />
          {/*
            ==================================================================
            THE FORM ON THE LEFT, THE KIOSK ON THE RIGHT.
            ==================================================================

            This was one column of sixteen stacked controls, of which EIGHT were
            title/body pairs for four languages — a wall an operator scrolled
            past to reach a send button, during an emergency, which is the one
            situation this screen exists for.

            Two changes carry most of the improvement:

              · The four languages are TABS, not eight fields. Only uz-Latn is
                required (§6.4: the others fall back to it), so showing all four
                at once spends three quarters of the form on the optional case.
                A dot on a tab says which ones are written.

              · The preview is the KIOSK'S OWN SHAPE — 9:16, the takeover
                filling it, at the position the citizen reads it. §6.5 asks for
                a preview «at true 1080×1920»; a wide letterboxed banner was not
                that, and it made an emergency takeover look like a web alert.
          */}
          <div className="grid gap-6 px-4 py-4 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4">
              {/*
                §6.4's three treatments as cards, not pills. An operator picking
                one is choosing whether to INTERRUPT a citizen mid-task, and the
                consequence belongs beside each option rather than on one line
                below that changes after you have already chosen.
              */}
              <fieldset>
                <legend className="mb-2 text-label text-ink-300">
                  {t('broadcast.severityLabel')}
                </legend>
                <div className="grid gap-3 sm:grid-cols-3">
                  {ANNOUNCEMENT_SEVERITIES.map((option) => {
                    const chosen = option === severity;
                    return (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={chosen}
                        onClick={() => setSeverity(option)}
                        className={`flex flex-col gap-1.5 rounded-card border p-3 text-left transition-shadow duration-state ${
                          chosen
                            ? 'border-brand bg-panel shadow-lift'
                            : 'border-hairline bg-panel shadow-card hover:shadow-lift'
                        }`}
                      >
                        <span className={`text-label font-semibold ${accentClass(option)}`}>
                          {severityLabel(option)}
                        </span>
                        <span className="text-micro leading-snug text-ink-500">
                          {t(`broadcast.treatment.${option}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/*
                THE LANGUAGE TABS. The dot marks a language that has a title —
                so «which of these have I actually written» is visible without
                clicking through all four, which is the question somebody asks
                immediately before sending.
              */}
              <div>
                <div className="mb-2 flex flex-wrap gap-1 border-b border-hairline">
                  {LOCALE_FIELDS.map((field) => {
                    const active = field.key === locale;
                    const filled = (title[field.key] ?? '').trim().length > 0;
                    return (
                      <button
                        key={field.key}
                        type="button"
                        onClick={() => setLocale(field.key)}
                        className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-label transition-colors duration-state ${
                          active
                            ? 'border-brand font-semibold text-ink-100'
                            : 'border-transparent text-ink-500 hover:text-ink-300'
                        }`}
                      >
                        {t(field.labelKey)}
                        {field.required ? <span className="text-crit">*</span> : null}
                        {filled ? <span className="h-1.5 w-1.5 rounded-full bg-ok" /> : null}
                      </button>
                    );
                  })}
                </div>

                {LOCALE_FIELDS.filter((field) => field.key === locale).map((field) => (
                  <div key={field.key} className="space-y-3">
                    <Field
                      label={t('broadcast.title')}
                      hint={field.required ? t('broadcast.fallbackNote') : undefined}
                    >
                      <TextInput
                        value={title[field.key] ?? ''}
                        onChange={(event) =>
                          setTitle((prev) => ({ ...prev, [field.key]: event.target.value }))
                        }
                      />
                    </Field>
                    <Field label={t('broadcast.body')}>
                      <TextInput
                        value={body[field.key] ?? ''}
                        onChange={(event) =>
                          setBody((prev) => ({ ...prev, [field.key]: event.target.value }))
                        }
                      />
                    </Field>
                  </div>
                ))}
              </div>

              {/*
                §6.4 — the missing languages, NAMED, before sending. Not an
                error: choosing to send uz-Latn only at 3 a.m. is a legitimate
                decision, and the operator is entitled to make it knowingly.
              */}
              {missing.length > 0 ? (
                <Notice tone="quiet">
                  {t('broadcast.missingLocales', { locales: missing.join(', ') })}
                </Notice>
              ) : null}

              {/*
                §6.4's optional image or video.

                THE RECOMMENDED SIZE IS PRINTED HERE, next to the picker,
                because an operator cannot be expected to know the kiosk is
                1080×1920 portrait. Without it the predictable thing happens:
                somebody attaches a landscape photo from a phone, it letterboxes
                into two black bars, and a notice meant to convey urgency looks
                like a mistake. The panel states the target, warns when the
                shape is far from it, and still accepts the file — a
                wrong-shaped image during an emergency beats no image.
              */}
              <Field
                label={t('broadcast.media')}
                hint={t('broadcast.mediaHint', {
                  width: String(KIOSK_SCREEN_WIDTH),
                  height: String(KIOSK_SCREEN_HEIGHT),
                })}
              >
                <input
                  type="file"
                  accept={[...ANNOUNCEMENT_IMAGE_MIME, ...ANNOUNCEMENT_VIDEO_MIME].join(',')}
                  className="block w-full text-label text-ink-300 file:mr-3 file:rounded file:border-0 file:bg-panel-raised file:px-3 file:py-1.5 file:text-label file:text-ink-100"
                  onChange={(event) => void pickMedia(event.target.files?.[0])}
                />
                {uploading ? (
                  <p className="mt-2 text-micro text-ink-500">{t('broadcast.mediaUploading')}</p>
                ) : null}
                {heavy ? <p className="mt-2 text-micro text-warn">{t('broadcast.mediaHeavy')}</p> : null}
                {media ? (
                  <div className="mt-2 flex items-center gap-3">
                    <span className="text-micro text-ink-300">
                      {t(`broadcast.mediaKind.${media.kind}`)}
                      {media.width && media.height ? ` · ${media.width}×${media.height}` : ''}
                    </span>
                    <Button tone="quiet" onClick={() => setMedia(null)}>
                      {t('broadcast.mediaRemove')}
                    </Button>
                  </div>
                ) : null}
                {/* Landscape, or far off 9:16. Stated as a warning, never a
                    block: the operator is the one who knows whether the picture
                    matters more than the letterboxing. */}
                {media?.width && media.height && media.width > media.height ? (
                  <p className="mt-2 text-micro text-warn">{t('broadcast.mediaLandscape')}</p>
                ) : null}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('broadcast.expiresIn')} hint={t('broadcast.expiryNote')}>
                  <TextInput
                    inputMode="numeric"
                    value={hours}
                    onChange={(event) => setHours(event.target.value)}
                  />
                </Field>
                {/* The target is written in words, with the COUNT. An empty
                    selection that silently means «everywhere» is how somebody
                    broadcasts to the province believing they picked one
                    building — and a number is what makes the scale land. */}
                <Field label={t('broadcast.target')} hint={t('broadcast.targetNote')}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-figure font-bold tabular-nums leading-none text-ink-100">
                      {targetCount}
                    </span>
                    <span className="text-label text-ink-300">{t('broadcast.targetAll')}</span>
                  </div>
                </Field>
              </div>

              <Button onClick={() => void send()} disabled={busy}>
                {t('broadcast.send')}
              </Button>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* §6.5's PREVIEW, at the kiosk's own shape                    */}
            {/* ---------------------------------------------------------- */}
            <div className="space-y-3">
              <p className="text-nano font-semibold uppercase text-ink-500">
                {t('broadcast.preview')}
              </p>

              {/*
                9:16, because that is the glass — and ALL THREE severities fill
                it, since the owner's 2026-08-19 ruling that every eʼlon is a
                big-screen one. What the preview now shows differing between
                them is the thing that still differs: whether a citizen can put
                it aside.
              */}
              <div className="relative aspect-[9/16] w-full overflow-hidden rounded-card border border-hairline bg-canvas">
                {media ? (
                  media.kind === 'image' ? (
                    <img src={media.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <video
                      src={media.url}
                      className="absolute inset-0 h-full w-full object-cover"
                      muted
                      loop
                      autoPlay
                      playsInline
                    />
                  )
                ) : null}

                <div
                  className={`absolute inset-0 flex flex-col items-center justify-center px-4 py-5 text-center ${
                    severity === 'emergency'
                      ? 'bg-crit/90'
                      : severity === 'warning'
                        ? 'bg-warn/90'
                        : 'bg-info/90'
                  }`}
                >
                  <p className="text-nano font-semibold uppercase tracking-wide text-white/85">
                    {severityLabel(severity)}
                  </p>
                  <p className="mt-2 text-lead font-bold leading-tight text-white">
                    {title.uz_latn?.trim() || t('broadcast.previewEmptyTitle')}
                  </p>
                  <p className="mt-1 text-micro leading-relaxed text-white/90">
                    {body.uz_latn?.trim() || t('broadcast.previewEmptyBody')}
                  </p>
                  {/* The dismiss affordance, previewed. Read from the SAME
                      presentation table the API sends to the kiosk, so this can
                      never claim a button the machine will not draw. It is the
                      single
                      most consequential difference between the two — an
                      operator should see it here rather than discover it from a
                      citizen who could not get past the screen. */}
                  {ANNOUNCEMENT_PRESENTATION[severity].dismissible ? (
                    <span className="mt-4 inline-block rounded border border-white/50 px-3 py-1 text-nano uppercase text-white">
                      {t('broadcast.previewDismiss')}
                    </span>
                  ) : null}
                </div>
              </div>

              <p className="text-nano leading-relaxed text-ink-500">
                {t('broadcast.previewNote', {
                  width: String(KIOSK_SCREEN_WIDTH),
                  height: String(KIOSK_SCREEN_HEIGHT),
                })}
              </p>
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader title={t('broadcast.historyTitle')} aside={t('broadcast.historyHint')} />
        <div className="px-4 py-3">
          {items === null ? (
            <Skeleton className="h-24" />
          ) : items.length === 0 ? (
            <EmptyState message={t('broadcast.empty')} />
          ) : (
            /*
              CARDS, NOT RUN-ON ROWS.

              A row read «Ogohlantirish Xivada Prezidentimiz… — STAGING
              Administrator · 18/08, 16:13 · olib tashlandi» and then a delivery
              line, all at the same weight, wrapping wherever it happened to. On
              an emergency this is the screen somebody scans to answer «did it
              land», and nothing about that layout answered it.

              Now each notice is a card with a severity bar down its left edge,
              its title, who sent it and when, and — the part that matters — the
              delivery split as two figures with the PENDING kiosks named. §6.4
              is explicit that the pending list is the louder half: for an
              evacuation, the screens that have not confirmed are the buildings
              somebody has to walk to, and a delivered count says nothing about
              them.
            */
            <div className="grid gap-3 xl:grid-cols-2">
              {items.map((item) => {
                const severity = item.severity as AnnouncementSeverity;
                const delivered = item.delivered?.length ?? 0;
                const pending = item.pending ?? [];
                const total = delivered + pending.length;
                return (
                  <div
                    key={item.id}
                    className="flex overflow-hidden rounded-card border border-hairline bg-panel shadow-card"
                  >
                    {/* The severity as a bar, not a word in the sentence. Same
                        device as the fleet rows, and a background colour on its
                        own element so it cannot lose a cascade fight. */}
                    <span
                      className={`w-1 shrink-0 ${BAR_OF[severity] ?? 'bg-hairline'}`}
                      aria-hidden="true"
                    />
                    {/*
                      THE THUMBNAIL WAS ALREADY IN THE RESPONSE.
                      `GET /mbt/announcements` has returned `item.media` since
                      the route shipped — the composer above reads the same
                      shape to build its own 9:16 preview (`media.url`,
                      `media.kind`). This card just never looked at it, so an
                      operator scanning history for "which one was the flood
                      photo" had eight identical text rows to open one by one.

                      Square, not 9:16: the card is a scan target, not the
                      broadcast itself — the full-shape preview stays in the
                      composer, where an operator is deciding what to publish.
                    */}
                    {item.media ? (
                      <div className="h-16 w-16 shrink-0 overflow-hidden bg-canvas">
                        {item.media.kind === 'image' ? (
                          <img
                            src={item.media.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="relative h-full w-full">
                            <video
                              src={item.media.url}
                              className="h-full w-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                            {/* First frame renders as the poster with no JS;
                                this glyph is the only cue it plays. */}
                            <span className="absolute inset-0 flex items-center justify-center">
                              <svg
                                viewBox="0 0 24 24"
                                className="h-5 w-5 fill-white/90 drop-shadow"
                                aria-hidden="true"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null}
                    <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`text-nano font-semibold uppercase tracking-wide ${accentClass(severity)}`}>
                            {severityLabel(item.severity)}
                            {item.live ? ` · ${t('broadcast.liveNow')}` : ''}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-label font-semibold leading-snug text-ink-100">
                            {item.title.uz_latn}
                          </p>
                        </div>
                        {mayPublish && item.live ? (
                          <Button tone="quiet" onClick={() => void takedown(item.id)} disabled={busy}>
                            {t('broadcast.takedown')}
                          </Button>
                        ) : null}
                      </div>

                      <p className="truncate text-micro text-ink-500">
                        {item.created_by_name} · {stamp(new Date(item.created_at))}
                        {item.taken_down_at ? ` · ${t('broadcast.takenDown')}` : ''}
                      </p>

                      {total > 0 ? (
                        <div className="mt-auto space-y-1.5 border-t border-hairline pt-2">
                          <div className="flex items-baseline justify-between gap-3 text-micro">
                            <span className="text-ink-500">{t('broadcast.deliveryLabel')}</span>
                            <span className="tabular-nums">
                              <span className="font-semibold text-ok">{delivered}</span>
                              <span className="text-ink-500"> / {total}</span>
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-hairline">
                            <div
                              className="h-full rounded-full bg-ok"
                              style={{ width: `${Math.round((delivered / total) * 100)}%` }}
                            />
                          </div>
                          {pending.length > 0 ? (
                            <p className="text-nano text-warn">
                              {t('broadcast.pending', {
                                list: pending.map((k) => k.short_code).join(', '),
                              })}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * The severity as a BACKGROUND colour, for the card's edge bar.
 *
 * Background rather than border, and its own element — the fleet grid learned
 * that the hard way: a border colour beside another border colour at equal
 * specificity is decided by stylesheet order, and it went different ways for
 * different colours.
 */
const BAR_OF: Record<string, string> = {
  emergency: 'bg-crit',
  warning: 'bg-warn',
  info: 'bg-info',
};

/** The optional locales, trimmed, with blanks dropped rather than sent empty. */
function cleaned(map: TextMap): TextMap {
  const out: TextMap = {};
  for (const field of LOCALE_FIELDS) {
    if (field.required) continue;
    const text = map[field.key]?.trim();
    if (text) out[field.key] = text;
  }
  return out;
}
